import { CheckCircleFilled, WarningFilled } from "@ant-design/icons"
import { Card, Divider, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import InfiniteScroll from "react-infinite-scroll-component"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import type { RootState } from "../../../store.ts"
import { NOTIFICATION_PATHS } from "../../Notifications/api/services.ts"
import { parseNotificationMessage } from "../../Notifications/helpers/parseNotificationMessage.ts"
import type {
  Notification,
  NotificationLevel
} from "../../Notifications/types/notification.types.ts"

const { Title, Text, Paragraph } = Typography

const iconMap: Record<NotificationLevel, React.ReactNode> = {
  success: <CheckCircleFilled style={{ fontSize: 18, color: "#52c41a" }} />,
  warning: <WarningFilled style={{ fontSize: 18, color: "#faad14" }} />,
  error: <WarningFilled style={{ fontSize: 18, color: "#ff4d4f" }} />,
  info: <CheckCircleFilled style={{ fontSize: 18, color: "#1677ff" }} />,
  4: <WarningFilled style={{ fontSize: 18, color: "#ff4d4f" }} />
}

export function DashboardNotifications() {
  const { t } = useTranslation()
  const accessToken = useSelector((state: RootState) => state.auth.accessToken)
  const containerRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [nextId, setNextId] = useState<number | null>(null)
  const [unreadCount, setUnreadCount] = useState(0)

  const onLoadMore = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "fetch_more",
          after_id: nextId
        })
      )
    }
  }

  useEffect(() => {
    if (!hasMore) return

    const container = containerRef.current
    if (!container) return

    const hasNoScroll = container.scrollHeight <= container.clientHeight
    if (hasNoScroll && notifications.length > 0) {
      onLoadMore()
    }
  }, [notifications.length, hasMore])

  useEffect(() => {
    if (!accessToken) return

    wsRef.current = new WebSocket(
      NOTIFICATION_PATHS.getNotifications(accessToken)
    )

    wsRef.current.onmessage = (event) => {
      const data = parseNotificationMessage(event.data)
      if (!data) return

      if (data.type === "initial_state" && data.notifications) {
        setNotifications(data.notifications)
        setHasMore(data.has_more || false)
        setNextId(data.next_cursor)
        setUnreadCount(data.unread_count ?? 0)
      }

      if (data.type === "notifications_batch") {
        if (data.notifications) {
          setNotifications((prev) => [
            ...prev,
            ...(data.notifications as Notification[])
          ])
        }
        setNextId(data.next_cursor)
        setHasMore(data.has_more || false)
      }
    }

    return () => wsRef.current?.close()
  }, [accessToken])

  const getBackgroundColor = (notification: Notification) => {
    if (notification.level === 4) {
      return notification.unread ? "rgba(255, 162, 162, 0.12)" : "transparent"
    }
    return notification.unread ? "rgba(22, 119, 255, 0.04)" : "transparent"
  }

  return (
    <Card
      variant="borderless"
      className="h-full"
      classNames={{ body: "h-full flex flex-col p-3" }}
    >
      <Flex justify="space-between" align="center" className="pb-1.5">
        <Title className="mb-0" level={4}>
          {t("notifications.bell.title", { n: unreadCount })}
        </Title>
      </Flex>

      <div
        className="flex-1 overflow-auto tailwind-scroll"
        id="notificationsScrollableDiv"
        ref={containerRef}
      >
        {notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("notifications.bell.empty")}</Text>}
          />
        ) : (
          <InfiniteScroll
            dataLength={notifications.length}
            next={onLoadMore}
            hasMore={hasMore}
            className="flex flex-col gap-1"
            loader={<Skeleton active paragraph={{ rows: 1 }} />}
            endMessage={<Divider />}
            scrollableTarget="notificationsScrollableDiv"
          >
            {notifications.map((notification) => (
              <Flex
                align="start"
                gap="small"
                key={notification.id}
                className="p-2 rounded-card"
                style={{
                  backgroundColor: getBackgroundColor(notification)
                }}
              >
                <Flex
                  align="center"
                  justify="center"
                  className="size-6 shrink-0 mt-0.5"
                >
                  {iconMap[notification.level]}
                </Flex>
                <div className="flex-1 min-w-0">
                  <Paragraph
                    className={"mb-0"}
                    ellipsis={{ rows: 4, expandable: true, symbol: "more" }}
                    strong={notification.unread}
                    style={{ fontSize: 14, display: "block" }}
                  >
                    {notification.description}
                  </Paragraph>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("notifications.bell.timeAgo", { time: notification.timesince })}
                  </Text>
                </div>
                {notification.unread && (
                  <span className="size-2 rounded-full bg-primary shrink-0 mt-2" />
                )}
              </Flex>
            ))}
          </InfiniteScroll>
        )}
      </div>
    </Card>
  )
}
